(() => {
  const controller = window.OMOInfoPage;
  if (controller) {
    controller.initAll();
    return;
  }

  const rootSelector = '[data-omo-info-root]';
  const triggerSelector = '[data-omo-info-trigger]';
  const panelSelector = '[data-omo-info-panel]';

  const getHash = () => {
    const hash = window.location.hash.slice(1);

    try {
      return decodeURIComponent(hash);
    } catch (_error) {
      return hash;
    }
  };

  // One-off migration: the label-derived hash that shipped before anchors were made stable.
  const hashAliases = { 'exchanged-shipping': 'non-returnable-items' };

  const normalizeHash = () => {
    const stable = hashAliases[getHash()];
    if (!stable) return;

    const url = new URL(window.location.href);
    url.hash = stable;
    window.history.replaceState(window.history.state, '', url);
  };

  const getEntries = (root) => {
    const triggers = [...root.querySelectorAll(triggerSelector)];
    const panels = [...root.querySelectorAll(panelSelector)];
    const keys = [...new Set(triggers.map((trigger) => trigger.dataset.omoInfoTrigger))];

    return keys
      .map((key) => {
        const panel = panels.find((candidate) => candidate.dataset.omoInfoPanel === key);
        const relatedTriggers = triggers.filter((trigger) => trigger.dataset.omoInfoTrigger === key);

        return panel ? { key, triggers: relatedTriggers, panel } : null;
      })
      .filter(Boolean);
  };

  const setArrow = (root, selector, disabled) => {
    root.querySelectorAll(selector).forEach((button) => {
      button.disabled = disabled;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    });
  };

  const activate = (root, requestedKey, updateHistory = false) => {
    const entries = getEntries(root);
    if (entries.length === 0) return;

    const activeEntry = entries.find((entry) => entry.key === requestedKey) || entries[0];

    entries.forEach((entry) => {
      const isActive = entry === activeEntry;
      entry.triggers.forEach((trigger) => {
        if (trigger.getAttribute('role') === 'tab') {
          trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
        }
        trigger.classList.toggle('omo-info-page__nav-trigger--active', isActive);
      });
      entry.panel.hidden = !isActive;
    });

    // mobile navigator: same active entry, prev / next follow the block order and stop at the ends
    const index = entries.indexOf(activeEntry);
    root.querySelectorAll('[data-omo-info-carousel-label]').forEach((label) => {
      label.textContent = activeEntry.triggers[0].textContent.trim();
    });
    setArrow(root, '[data-omo-info-prev]', index === 0);
    setArrow(root, '[data-omo-info-next]', index === entries.length - 1);

    if (updateHistory && getHash() !== activeEntry.key) {
      const url = new URL(window.location.href);
      url.hash = activeEntry.key;
      window.history.pushState({ omoInfoPanel: activeEntry.key }, '', url);
    }
  };

  const initRoot = (root) => {
    if (root.dataset.omoInfoInitialized === 'true') return;

    const entries = getEntries(root);
    if (entries.length === 0) return;

    root.dataset.omoInfoInitialized = 'true';
    root.classList.add('omo-info-page--js');
    root.omoInfoActivate = (key, updateHistory = false) => activate(root, key, updateHistory);

    root.addEventListener('click', (event) => {
      const arrow = event.target.closest('[data-omo-info-prev], [data-omo-info-next]');
      if (arrow && root.contains(arrow)) {
        const entries = getEntries(root);
        const current = entries.findIndex((entry) => !entry.panel.hidden);
        const target = entries[current + (arrow.hasAttribute('data-omo-info-next') ? 1 : -1)];
        if (target) activate(root, target.key, true);
        return;
      }

      const trigger = event.target.closest(triggerSelector);
      if (!trigger || !root.contains(trigger)) return;

      activate(root, trigger.dataset.omoInfoTrigger, true);
    });

    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      const trigger = event.target.closest(triggerSelector);
      if (!trigger || !root.contains(trigger)) return;

      event.preventDefault();
      activate(root, trigger.dataset.omoInfoTrigger, true);
    });

    activate(root, getHash());
  };

  const initAll = (scope = document) => {
    normalizeHash();
    if (scope.matches?.(rootSelector)) initRoot(scope);
    scope.querySelectorAll?.(rootSelector).forEach(initRoot);
  };

  const syncFromHash = () => {
    normalizeHash();
    document.querySelectorAll(rootSelector).forEach((root) => {
      initRoot(root);
      root.omoInfoActivate?.(getHash());
    });
  };

  window.OMOInfoPage = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  window.addEventListener('popstate', syncFromHash);
  window.addEventListener('hashchange', syncFromHash);

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    if (!blockId) return;

    const panel = [...document.querySelectorAll(`${rootSelector} ${panelSelector}`)].find(
      (candidate) => candidate.dataset.omoInfoBlockId === blockId,
    );
    if (!panel) return;

    const root = panel.closest(rootSelector);
    initRoot(root);
    root.omoInfoActivate?.(panel.dataset.omoInfoPanel);
  });
})();
